import React from 'react';
import { NavLink } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../features/auth/authSlice'; // Import your auth logout action

const navBase = 'flex items-center gap-3 rounded-xl py-3 text-left font-medium transition-colors duration-200 w-full';
const navActive = 'bg-white/10 text-white';
const navIdle = 'text-slate-300 hover:bg-white/5 hover:text-white';

function Icon({ children, isLogout }) {
  return (
    <span className={`grid h-10 w-10 place-items-center rounded-2xl bg-slate-100 text-slate-900 ${isLogout ? 'bg-red-100 text-red-600' : ''}`}>
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-none stroke-current stroke-2">
        {children}
      </svg>
    </span>
  );
}

const menuItems = [
  { label: 'Dashboard', to: '/dashboard', permission: 'dashboard', icon: <path d="M4 12h6V4H4zm10 8h6v-12h-6zM4 20h6v-6H4zm10-14h6v4h-6z" /> },
  { label: 'Temperature', to: '/temperature', permission: 'temperature', icon: <path d="M4 12h6V4H4zm10 8h6v-12h-6zM4 20h6v-6H4zm10-14h6v4h-6z" /> },
  {
    label: 'Checks',
    to: '/checks',
    permission: 'checks',
    icon: (
      <>
        <path d="M7 4h10v16H7z" />
        <path d="M9 8h6M9 12h6M9 16h4" strokeLinecap="round" />
      </>
    ),
    submenu: [
      { label: 'Opening Check', to: '/checks/opening-check' },
      { label: 'Closing Check', to: '/checks/closing-check' },
      { label: 'Cleaning Check', to: '/checks/cleaning-check' },
      { label: 'Delivery Check', to: '/checks/delivery-check' },
      { label: 'Food Temperature Check', to: '/checks/temperature-check' },
      { label: 'Fire Safety Check', to: '/checks/fire-safety-check' },
      { label: 'Equipment Check', to: '/checks/equipment-check' },
      { label: 'Monthly Alergen Check', to: '/checks/allergen-check' },
      { label: 'Monthly Staff Training Check', to: '/checks/staff-training-check' },
      { label: 'PAT Monthly Check', to: '/checks/pat-check' },
    ],
  },
  {
    label: 'Rota',
    to: '/rota',
    permission: 'rota',
    icon: <path d="M12 4 2 9l10 5 10-5-10-5zm-6 8v4c0 1.1 2.7 3 6 3s6-1.9 6-3v-4" />,
    submenu: [
      { label: 'Opening Rota', to: '/rota/opening' },
      { label: 'Closing Rota', to: '/rota/closing' },
      { label: 'Cleaning Rota', to: '/rota/cleaning' },
      { label: 'Food Temperature Rota', to: '/rota/food-temperature' },
      { label: 'PAT Rota', to: '/rota/pat' },
      { label: 'Pest Control Rota', to: '/rota/pest-control' },
    ],
  },
  { label: 'Users', to: '/users', permission: 'users', icon: <path d="M8 12a4 4 0 1 0-0.01 0M16 11a3 3 0 1 0-0.01 0M4 20c0-3 2.5-5 6-5s6 2 6 5M14 20c0-2 1.5-3.5 4-4 1.5-.3 2.7.2 3.6 1.1" /> },
  { label: 'Equipment', to: '/equipment', permission: 'equipment', icon: <path d="M4 7h16v10H4zM7 7V5h10v2M8 11h8M8 14h5" /> },
  {
    label: 'Compliance Center',
    to: '/compliance-center',
    permission: 'compliance',
    icon: <path d="M7 3h10v18H7zM9 7h6M9 11h6M9 15h4" />,
    submenu: [
      { label: 'Overview Portal', to: '/compliance-center' },
      { label: 'Equipment Compliance', to: '/compliance-center/equipment' },
      { label: 'Fire Safety Compliance', to: '/compliance-center/fire-safety' },
      { label: 'Incident Compliance', to: '/compliance-center/incidents' },
    ],
  },
  { label: 'Staff Training', to: '/staff-training' , icon: <path d="M4 12h6V4H4zm10 8h6v-12h-6zM4 20h6v-6H4zm10-14h6v4h-6z" /> },
  // { label: 'Incident Log', to: '/incident-log', permission: 'users', icon: <path d="M12 4 2 9l10 5 10-5-10-5zm-6 8v4c0 1.1 2.7 3 6 3s6-1.9 6-3v-4" /> },
  { label: 'Incident Dashboard', to: '/incident', permission: 'users', icon: <path d="M12 4 2 9l10 5 10-5-10-5zm-6 8v4c0 1.1 2.7 3 6 3s6-1.9 6-3v-4" /> },
  { label: 'Audit Mode', to: '/checks/opening', permission: 'checks', icon: <path d="M4 7h16M7 7V5h10v2M7 7v12h10V7M9 11h6M9 15h4" /> },
  { label: 'Reporting', to: '/dashboard', permission: 'dashboard', icon: <path d="M4 19V5M4 19h16M8 16v-6M12 16V8M16 16v-3" /> },
  { label: 'Settings', to: '/settings', permission: 'settings', icon: <path d="M4 19V5M4 19h16M8 16v-6M12 16V8M16 16v-3" /> },
];

function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const dispatch = useDispatch();

  const handleLogout = () => {
    dispatch(logout());
  };

  return (
    <aside
      className={`
        fixed
        inset-y-0
        left-0
        z-50
        flex
        w-[300px]
        flex-col
        gap-8
        overflow-y-auto
        bg-slate-950
        px-6
        py-8
        text-slate-100
        shadow-2xl

        transform
        transition-transform
        duration-300
        ease-in-out

        lg:relative
        lg:z-auto
        lg:translate-x-0
        lg:shadow-none
        lg:min-h-screen

        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <img
          src="/assets/logo.png"
          alt="Aaziraone"
          className="rounded-2xl object-cover"
        />

        {/* Mobile / Tablet Close Button */}
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close sidebar"
          className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/10 text-white transition hover:bg-white/20 lg:hidden"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              d="M6 6l12 12M18 6L6 18"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <nav className="grid gap-2">
        {menuItems.map((item, index) => {
          if (item.submenu) {
            return (
              <div
                key={`${item.label}-${index}`}
                className="group relative"
              >
                <NavLink
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `${navBase} ${isActive ? navActive : navIdle}`
                  }
                >
                  <Icon>{item.icon}</Icon>
                  {item.label}
                </NavLink>

                <div className="invisible absolute left-[100px] top-[10px] z-50 ml-3 w-64 rounded-2xl border border-slate-200 bg-white p-2 text-slate-900 opacity-0 shadow-2xl transition group-hover:visible group-hover:opacity-100">
                  {item.submenu.map((subItem) => (
                    <NavLink
                      key={subItem.to}
                      to={subItem.to}
                      end={subItem.to === '/compliance-center'}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `block rounded-xl px-4 py-3 text-sm font-medium transition ${
                          isActive
                            ? 'bg-slate-950 text-white'
                            : 'text-slate-700 hover:bg-slate-100'
                        }`
                      }
                    >
                      {subItem.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          }

          return (
            <NavLink
              key={`${item.label}-${index}`}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `${navBase} ${isActive ? navActive : navIdle}`
              }
            >
              <Icon>{item.icon}</Icon>
              {item.label}
            </NavLink>
          );
        })}

        {/* Log Out */}
        <button
          onClick={handleLogout}
          className={`${navBase} ${navIdle} mt-2 text-red-400 hover:bg-red-500/10 hover:text-red-500`}
        >
          <Icon isLogout={true}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7 21h6a2.25 2.25 0 0 0 2.25-2.25V15M12 9l-3 3m0 0 3 3m-3-3h12.75"
            />
          </Icon>
          Log Out
        </button>
      </nav>
    </aside>
  );
}

export default Sidebar;
